import { graphql, useStaticQuery } from 'gatsby';
import Colors from '@components/shared/Colors';
import { PageOptions, NavigationItem, NavigationPreview, NavigationSection, ProcessedNavigationData } from '@contracts';
import { useCountry } from '@hooks/useCountry';
import { filterShowInCountries } from '@utils/country';
import useLanguageData from '@utils/useLanguageData';

export const query = graphql`
  query getNavigation {
    allPrismicEcommerceModernNavigation {
      nodes {
        lang
        data {
          retailers {
            icon_svg {
              gatsbyImageData
              url
              alt
            }
            link
            show_in_countries
          }
          partners {
            icon_svg {
              gatsbyImageData
              url
              alt
            }
            title {
              html
              text
              richText
            }
            show_in_countries
          }
          product_categories
          vitals_article_uids
          period_care_title
          period_care_shop_all
          period_care {
            title
            link
            on_sale
            is_new
            show_in_countries
            text_color
          }
          vaginal_health_title
          vaginal_health_shop_all
          vaginal_health {
            title
            link
            on_sale
            is_new
            show_in_countries
            text_color
          }
          accessories_title
          accessories_shop_all
          accessories {
            title
            link
            on_sale
            is_new
            show_in_countries
            text_color
          }
          about_us_title
          about_us {
            title
            link
            on_sale
            is_new
            show_in_countries
            text_color
          }
          discover_title
          discover {
            title
            link
            on_sale
            is_new
            show_in_countries
            text_color
          }
          footer {
            title
            link
            show_in_countries
          }
          policies {
            title
            link
            show_in_countries
          }
        }
      }
    }
    allPrismicEcommerceProductCategory(sort: { data: { category_nav_order: ASC } }) {
      nodes {
        data {
          title
          category_description
          category_color
          text_color
          category_image {
            url
            alt
          }
        }
        uid
        lang
      }
    }
  }
`;

export const getNavigationData = (): ProcessedNavigationData => {
  const { country } = useCountry();
  const queryResult = useStaticQuery(query);
  const { allPrismicEcommerceModernNavigation, allPrismicEcommerceProductCategory } = queryResult;
  const { nodes: modernNavigationNodes } = allPrismicEcommerceModernNavigation;
  const { pageData: modernNavigation } = useLanguageData(modernNavigationNodes);
  const modernNavigationData = modernNavigation?.data;
  const vitalsArticleUids = modernNavigationData?.vitals_article_uids?.split(',') || [];

  const periodCare: NavigationSection = {
    title: modernNavigationData?.period_care_title,
    shopAllLink: modernNavigationData?.period_care_shop_all,
    items: filterShowInCountries<NavigationItem>(modernNavigationData?.period_care, country)
  };
  const vaginalHealth = {
    title: modernNavigationData?.vaginal_health_title,
    shopAllLink: modernNavigationData?.vaginal_health_shop_all,
    items: filterShowInCountries<NavigationItem>(modernNavigationData?.vaginal_health, country)
  };
  const accessories = {
    title: modernNavigationData?.accessories_title,
    shopAllLink: modernNavigationData?.accessories_shop_all,
    items: filterShowInCountries<NavigationItem>(modernNavigationData?.accessories, country)
  };
  const aboutUs = {
    title: modernNavigationData?.about_us_title,
    items: filterShowInCountries<NavigationItem>(modernNavigationData?.about_us, country)
  };
  const discover = {
    title: modernNavigationData?.discover_title,
    items: filterShowInCountries<NavigationItem>(modernNavigationData?.discover, country)
  };
  const footer = {
    items: filterShowInCountries<NavigationItem>(modernNavigationData?.footer, country)
  };
  const policies = {
    title: modernNavigationData?.policies_title,
    items: filterShowInCountries<NavigationItem>(modernNavigationData?.policies, country)
  };
  const retailers = {
    items: filterShowInCountries<NavigationItem>(modernNavigationData?.retailers, country)
  };
  const partners = {
    items: filterShowInCountries<NavigationItem>(modernNavigationData?.partners, country)
  };

  const productCategories: NavigationPreview[] = [];
  const { nodes: productCategoryNodes } = allPrismicEcommerceProductCategory;
  const productCategoryIdsToFilterBy = modernNavigationData?.product_categories?.split(',') || [];
  const productCategoryFilteredData = productCategoryNodes?.filter((node: any) => productCategoryIdsToFilterBy.includes(node.uid));
  const { filterNodesByLanguage } = useLanguageData(productCategoryFilteredData);
  const productCategoryData = filterNodesByLanguage();

  productCategoryData.forEach((individualProductCategoryNode: any) => {
    productCategories.push({
      title: individualProductCategoryNode.data.category_description,
      link: `/${PageOptions.Products}/${individualProductCategoryNode.uid}`,
      image: individualProductCategoryNode.data.category_image.url,
      imageAlt: individualProductCategoryNode.data.category_image.alt,
      badgeText: individualProductCategoryNode.data.title,
      badgeTextColor: Colors.navyBlue,
      badgeBackground: individualProductCategoryNode.data.category_color
    });
  });

  return {
    periodCare,
    vaginalHealth,
    accessories,
    aboutUs,
    discover,
    footer,
    retailers,
    partners,
    policies,
    productCategories,
    vitalsArticleUids
  };
};
